import { DBSchema, IDBPDatabase, openDB } from "idb";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
} from "react";
import { To } from "react-router-dom";

import {
  IContextDialogPcStateForm,
  IProviderDialogPcProps,
} from "@/screens/app/components/dialogs/dialog_pc/context";
import { IContextPostStateForm } from "@/screens/app/screens/post/context";

export enum EIDBObjectStore {
  POST = "post",
  POST_V3 = "post_v3",
  PAYMENT_SUCCESS_ACTION = "payment_success_action",
}

type PaymentSuccessAction =
  | {
      type: "redirect";
      value: To;
      state?: Record<string, unknown>;
    }
  | {
      type: "post_constructor_v3";
      value: Partial<Pick<IProviderDialogPcProps, "mediaSet">>;
    }
  | {
      type: "download";
      value: Array<{
        dataURL: string;
        filename: string;
      }>;
    };

interface MyDBV extends DBSchema {
  [EIDBObjectStore.POST]: { key: number; value: IContextPostStateForm };
  [EIDBObjectStore.POST_V3]: {
    key: number | string;
    value: IContextDialogPcStateForm;
  };
  [EIDBObjectStore.PAYMENT_SUCCESS_ACTION]: {
    key: number;
    value: PaymentSuccessAction;
  };
}

type IContextIndexedDb = {
  promise: Promise<IDBPDatabase<MyDBV> | undefined>;
};

export const ContextIndexedDb = createContext<IContextIndexedDb>(
  null as unknown as IContextIndexedDb,
);

const VERSION = 3;

export const ProviderIndexedDb = ({ children }: PropsWithChildren<{}>) => {
  const ref = useRef<Promise<IDBPDatabase<MyDBV> | undefined>>(
    new Promise((resolve) => {
      if (import.meta.env.SSR) {
        return resolve(undefined);
      }

      openDB<MyDBV>(`smmplanner`, VERSION, {
        async upgrade(db) {
          Object.values(EIDBObjectStore).forEach((value) => {
            if (!db.objectStoreNames.contains(value)) {
              db.createObjectStore(value);
            }
          });
        },
      })
        .then(resolve)
        .catch(() => {
          resolve(undefined);
        });
    }),
  );

  return (
    <ContextIndexedDb.Provider
      value={{
        promise: ref.current,
      }}
    >
      {children}
    </ContextIndexedDb.Provider>
  );
};

export const useContextIndexedDb = <TObjectStore extends EIDBObjectStore>(
  objectStore: TObjectStore,
  globalDebounce?: number,
) => {
  const timeoutId = useRef(0);
  const { promise } = useContext(ContextIndexedDb);

  const get = useCallback(
    async (
      key: MyDBV[TObjectStore]["key"],
    ): Promise<MyDBV[TObjectStore]["value"] | undefined> => {
      try {
        return (await promise)?.get(objectStore, key);
      } catch (e) {
        return undefined;
      }
    },
    [objectStore, promise],
  );

  const save = useCallback(
    (
      value: MyDBV[TObjectStore]["value"],
      key: MyDBV[TObjectStore]["key"],
      debounce?: number,
    ) => {
      window.clearTimeout(timeoutId.current);

      timeoutId.current = window.setTimeout(async () => {
        (await promise)?.put(objectStore, value, key);
      }, debounce || globalDebounce || 0);
    },
    [globalDebounce, objectStore, promise],
  );

  const remove = useCallback(
    async (key: MyDBV[TObjectStore]["key"]) =>
      (await promise)?.delete(objectStore, key),
    [objectStore, promise],
  );

  const clear = useCallback(
    async () => (await promise)?.clear(objectStore),
    [objectStore, promise],
  );

  return {
    clear,
    get,
    remove,
    save,
  };
};
